import type {
  CcsConfiguration,
  EngineeringVerdict,
  GenerationAssumption,
  GenerationImpactResult,
  GenerationImpactScenarioInput,
  GenerationUnit,
  NoiseDatum,
} from '../../types/generationImpact';
import { applyGenerationEquipmentEnvelope } from './generationEquipmentCatalog';
import {
  activeGenerationEquipmentSelection,
  resizeGenerationEquipmentFleet,
  type ActiveGenerationEquipmentSelection,
} from './equipmentSelectionState';
import { setCcsEnabledWithPlanningDefaults } from './planningCatalog';
import {
  GRID_SUPPLY_GENERATION_SIZING_NOT_APPLICABLE,
  isGridImportOnlyScenario,
  validateGenerationSizingPhysicalInputs,
} from './generationSizingValidation';
import { solveGenerationImpact } from './solveGenerationImpact';

export const GENERATION_ALTERNATIVES_MODEL_VERSION = 'generation-alternatives-v1' as const;
export const DEFAULT_GENERATION_ALTERNATIVE_BLOCK_COUNTS = [2, 3, 4] as const;
export const DEFAULT_GENERATION_ALTERNATIVE_CCS_MODES = [false, true] as const;
export const DEFAULT_GENERATION_ALTERNATIVE_SIZING_INCREMENT_MW = 10;

const MAX_BLOCK_COUNT = 1_000;
const MAX_ISO_GROSS_MW_PER_UNIT = 10_000;
const MAX_RESERVE_MARGIN_PCT = 100;
const ROOT_TOLERANCE_MW = 1e-7;
const ROOT_ITERATIONS = 80;
const RELATIVE_TOLERANCE = 1e-9;

export interface GenerationBlockSizingOptions {
  /** Defaults to the physical block count in the entered uniform fleet. */
  blockCount?: number;
  /** Defaults to the entered CCS mode. Enabling an all-zero placeholder hydrates planning defaults. */
  ccsEnabled?: boolean;
  /** Screening-size round-up. Defaults to 10 MW. */
  sizingIncrementMW?: number;
  /** Hard fail-closed search ceiling. Defaults to 10,000 MW per block. */
  maxIsoGrossMWPerUnit?: number;
}

export interface GenerationSizingCriterion {
  criterion: 'n-1' | 'n-1-plus-reserve';
  requiredNetMW: number;
  exactMinimumIsoGrossMWPerUnit: number;
  roundedIsoGrossMWPerUnit: number;
  roundedNMinusOneNetMW: number;
  roundedMarginMW: number;
  roundedVerdict: EngineeringVerdict;
}

export interface AvailableGenerationBlockSizing {
  status: 'available';
  modelVersion: typeof GENERATION_ALTERNATIVES_MODEL_VERSION;
  blockCount: number;
  onlineBlocksAfterContingency: number;
  ccsEnabled: boolean;
  technology: GenerationUnit['technology'];
  auxiliaryFractionOfIsoGross: number;
  sizingIncrementMW: number;
  nMinusOne: GenerationSizingCriterion;
  nMinusOnePlusReserve: GenerationSizingCriterion;
  heldConstantDisclosures: readonly string[];
}

export interface InvalidGenerationSizing {
  status: 'invalid';
  modelVersion: typeof GENERATION_ALTERNATIVES_MODEL_VERSION;
  errors: readonly string[];
}

export type GenerationBlockSizingResult =
  | AvailableGenerationBlockSizing
  | InvalidGenerationSizing;

export interface GenerationAlternativesOptions {
  /** Defaults to [2, 3, 4]. Values are de-duplicated and sorted ascending. */
  candidateBlockCounts?: readonly number[];
  /** Defaults to [false, true]. False is always ordered before true. */
  ccsModes?: readonly boolean[];
  /** Defaults to 10 MW. Each optimized row is rounded up, then re-solved. */
  sizingIncrementMW?: number;
  /** Hard fail-closed search ceiling. Defaults to 10,000 MW per block. */
  maxIsoGrossMWPerUnit?: number;
}

export interface GenerationAlternativeRow {
  id: string;
  kind: 'current' | 'optimized' | 'equipment-count';
  /** Set on `equipment-count` rows: the fixed envelope's model name. */
  equipmentModel?: string;
  label: string;
  blockCount: number;
  ccsEnabled: boolean;
  isoGrossMWPerUnit: number;
  totalIsoGrossMW: number;
  /** Null only for the entered/current row, which is not automatically resized. */
  exactReserveMinimumIsoGrossMWPerUnit: number | null;
  sizingIncrementMW: number | null;
  /** Exact, detached scenario evaluated for this row; safe for Apply and hashing. */
  input: GenerationImpactScenarioInput;
  /** Exact, detached canonical solver result paired with `input`. */
  result: GenerationImpactResult;
  capacity: GenerationImpactResult['capacity'];
  gas: GenerationImpactResult['gas'];
  water: GenerationImpactResult['water'];
  emissions: GenerationImpactResult['emissions'];
  footprint: GenerationImpactResult['footprint'];
  gaps: readonly string[];
  warnings: readonly string[];
}

export interface AvailableGenerationAlternativesMatrix {
  status: 'available';
  modelVersion: typeof GENERATION_ALTERNATIVES_MODEL_VERSION;
  sourceScenarioName: string;
  sizingIncrementMW: number;
  heldConstantDisclosures: readonly string[];
  rows: readonly GenerationAlternativeRow[];
}

export interface InvalidGenerationAlternativesMatrix {
  status: 'invalid';
  modelVersion: typeof GENERATION_ALTERNATIVES_MODEL_VERSION;
  errors: readonly string[];
}

export type GenerationAlternativesMatrixResult =
  | AvailableGenerationAlternativesMatrix
  | InvalidGenerationAlternativesMatrix;

export interface UniformBasis {
  template: GenerationUnit;
  currentBlockCount: number;
  auxiliaryFractionOfIsoGross: number;
}

interface NormalizedSizingOptions {
  blockCount: number;
  ccsEnabled: boolean;
  sizingIncrementMW: number;
  maxIsoGrossMWPerUnit: number;
}

interface SizingSolution {
  exactMinimumIsoGrossMWPerUnit: number;
  roundedIsoGrossMWPerUnit: number;
  roundedResult: GenerationImpactResult;
}

function nearlyEqual(left: number | undefined, right: number | undefined): boolean {
  if (left == null || right == null) return left === right;
  const scale = Math.max(1, Math.abs(left), Math.abs(right));
  return Math.abs(left - right) <= RELATIVE_TOLERANCE * scale;
}

function cloneScenario(input: GenerationImpactScenarioInput): GenerationImpactScenarioInput {
  return structuredClone(input);
}

export function physicalBlockCount(input: GenerationImpactScenarioInput): number {
  return input.units.reduce((sum, unit) => sum + unit.count, 0);
}

function normalizedCcs(current: CcsConfiguration, enabled: boolean): CcsConfiguration {
  return setCcsEnabledWithPlanningDefaults(current, enabled);
}

function ccsEngineeringBasisChanged(
  current: CcsConfiguration,
  candidate: CcsConfiguration,
): boolean {
  return !Object.is(current.captureRate, candidate.captureRate)
    || !Object.is(current.specificPowerKWhPerTonneCaptured, candidate.specificPowerKWhPerTonneCaptured)
    || !Object.is(current.waterWithdrawalGalPerTonneCaptured, candidate.waterWithdrawalGalPerTonneCaptured)
    || !Object.is(current.waterConsumptionGalPerTonneCaptured, candidate.waterConsumptionGalPerTonneCaptured)
    || !Object.is(current.footprintAcres, candidate.footprintAcres)
    || !Object.is(current.compressionFootprintAcres, candidate.compressionFootprintAcres);
}

function replaceCcsArchetypeAssumption(
  assumptions: readonly GenerationAssumption[],
  replacement: GenerationAssumption,
): GenerationAssumption[] {
  const firstIndex = assumptions.findIndex(assumption => assumption.field === 'ccsArchetype');
  if (firstIndex < 0) return [...assumptions, replacement];
  return assumptions.flatMap((assumption, index) => {
    if (assumption.field !== 'ccsArchetype') return [assumption];
    return index === firstIndex ? [replacement] : [];
  });
}

function ccsAssumptionsForCandidate(
  source: GenerationImpactScenarioInput,
  candidateCcs: CcsConfiguration,
): GenerationAssumption[] {
  const basisHydrated = ccsEngineeringBasisChanged(source.ccs, candidateCcs);
  const modeChanged = source.ccs.enabled !== candidateCcs.enabled;
  if (!basisHydrated && !modeChanged) return structuredClone(source.assumptions);

  const replacement: GenerationAssumption = basisHydrated
    ? {
        field: 'ccsArchetype',
        value: true,
        source: 'planning-default',
        label: 'Capture-through-compression planning archetype',
        citation: 'DOE/NETL planning basis; project-specific FEED required.',
        confidence: 'low',
      }
    : {
        field: 'ccsArchetype',
        value: candidateCcs.enabled,
        source: 'planning-default',
        label: 'Capture mode for generation capacity alternative',
        citation: 'Platform generation-alternatives screen; confirm the selected capture mode and retained CCS basis before FEED.',
        confidence: 'low',
      };
  return replaceCcsArchetypeAssumption(structuredClone(source.assumptions), replacement);
}

function auxiliaryFraction(unit: GenerationUnit): number {
  return unit.auxiliaryLoadMWPerUnit / unit.isoGrossMWPerUnit;
}

function acousticDatumEqual(
  left: NoiseDatum | undefined,
  right: NoiseDatum | undefined,
): boolean {
  if (left == null || right == null) return left === right;
  if (left.kind !== right.kind || !nearlyEqual(left.levelDbA, right.levelDbA)) return false;
  if (left.kind === 'sound-power' && right.kind === 'sound-power') {
    return nearlyEqual(left.directivityCorrectionDb, right.directivityCorrectionDb);
  }
  if (left.kind === 'reference-spl' && right.kind === 'reference-spl') {
    return nearlyEqual(left.referenceDistanceM, right.referenceDistanceM)
      && left.scope === right.scope
      && left.basisLabel === right.basisLabel;
  }
  return false;
}

export function uniformFleetBasis(input: GenerationImpactScenarioInput): {
  basis: UniformBasis | null;
  errors: string[];
} {
  if (isGridImportOnlyScenario(input)) {
    return { basis: null, errors: [GRID_SUPPLY_GENERATION_SIZING_NOT_APPLICABLE] };
  }
  const errors: string[] = [];
  const template = input.units[0];
  if (!template) {
    return { basis: null, errors: ['At least two independent generation blocks are required.'] };
  }
  if (input.units.length < 2) {
    errors.push('At least two independent generation blocks are required.');
  }
  if (input.units.some((unit) => unit.count !== 1)) {
    errors.push('Alternatives require one physical block per unit row (count = 1).');
  }
  if (new Set(input.units.map((unit) => unit.contingencyGroupId)).size !== input.units.length) {
    errors.push('Each generation block must have an independent contingency group.');
  }
  if (!Number.isFinite(template.isoGrossMWPerUnit) || template.isoGrossMWPerUnit <= 0) {
    errors.push('The entered ISO gross output per block must be finite and greater than zero.');
  }
  errors.push(...validateGenerationSizingPhysicalInputs(input));

  const templateAuxiliaryFraction = auxiliaryFraction(template);
  const isUniform = input.units.every((unit) => (
    unit.technology === template.technology
    && nearlyEqual(unit.isoGrossMWPerUnit, template.isoGrossMWPerUnit)
    && nearlyEqual(unit.ambientFactor, template.ambientFactor)
    && nearlyEqual(unit.degradationFactor, template.degradationFactor)
    && nearlyEqual(auxiliaryFraction(unit), templateAuxiliaryFraction)
    && nearlyEqual(unit.heatRateBtuPerKWhHhv, template.heatRateBtuPerKWhHhv)
    && nearlyEqual(unit.forcedOutageRate, template.forcedOutageRate)
    && acousticDatumEqual(unit.noise, template.noise)
    && nearlyEqual(unit.soundPowerDbA, template.soundPowerDbA)
    && nearlyEqual(unit.directivityCorrectionDb, template.directivityCorrectionDb)
    && nearlyEqual(unit.barrierLossDb, template.barrierLossDb)
    && nearlyEqual(unit.noxLbPerMWh, template.noxLbPerMWh)
    && nearlyEqual(unit.coLbPerMWh, template.coLbPerMWh)
  ));
  if (!isUniform) {
    errors.push('Alternatives require a uniform fleet basis across technology, rating, derates, auxiliaries, heat rate, outage rate, acoustic inputs, and emissions factors.');
  }

  return {
    basis: errors.length === 0
      ? {
          template,
          currentBlockCount: physicalBlockCount(input),
          auxiliaryFractionOfIsoGross: templateAuxiliaryFraction,
        }
      : null,
    errors,
  };
}

function validateSource(input: GenerationImpactScenarioInput): {
  basis: UniformBasis | null;
  sourceResult: GenerationImpactResult;
  errors: string[];
} {
  const sourceResult = solveGenerationImpact(cloneScenario(input));
  const { basis, errors } = uniformFleetBasis(input);
  const criticalLoadMW = input.criticalLoadMW ?? input.facilityLoadMW;
  if (!Number.isFinite(criticalLoadMW) || criticalLoadMW <= 0) {
    errors.push('Critical load must be finite and greater than zero for N-1 sizing.');
  }
  if (
    input.reserveMarginPct != null
    && (!Number.isFinite(input.reserveMarginPct)
      || input.reserveMarginPct < 0
      || input.reserveMarginPct > MAX_RESERVE_MARGIN_PCT)
  ) {
    errors.push(`Reserve margin must be finite and between 0% and ${MAX_RESERVE_MARGIN_PCT}%.`);
  }
  if (sourceResult.gaps.length > 0) {
    errors.push(...sourceResult.gaps.map((gap) => `Scenario input gap: ${gap}`));
  }
  return { basis, sourceResult, errors: [...new Set(errors)] };
}

function validatePositiveOption(
  value: number,
  label: string,
  maximum: number,
  errors: string[],
): void {
  if (!Number.isFinite(value) || value <= 0 || value > maximum) {
    errors.push(`${label} must be finite, greater than zero, and no greater than ${maximum}.`);
  }
}

function normalizeSizingOptions(
  basis: UniformBasis,
  input: GenerationImpactScenarioInput,
  options: GenerationBlockSizingOptions,
): { value: NormalizedSizingOptions | null; errors: string[] } {
  const blockCount = options.blockCount ?? basis.currentBlockCount;
  const ccsEnabled = options.ccsEnabled ?? input.ccs.enabled;
  const sizingIncrementMW = options.sizingIncrementMW
    ?? DEFAULT_GENERATION_ALTERNATIVE_SIZING_INCREMENT_MW;
  const maxIsoGrossMWPerUnit = options.maxIsoGrossMWPerUnit
    ?? MAX_ISO_GROSS_MW_PER_UNIT;
  const errors: string[] = [];
  if (!Number.isInteger(blockCount) || blockCount < 2 || blockCount > MAX_BLOCK_COUNT) {
    errors.push(`Block count must be an integer between 2 and ${MAX_BLOCK_COUNT}.`);
  }
  if (typeof ccsEnabled !== 'boolean') {
    errors.push('CCS mode must be boolean.');
  }
  validatePositiveOption(
    sizingIncrementMW,
    'Sizing increment',
    MAX_ISO_GROSS_MW_PER_UNIT,
    errors,
  );
  validatePositiveOption(
    maxIsoGrossMWPerUnit,
    'Maximum ISO gross output per block',
    MAX_ISO_GROSS_MW_PER_UNIT,
    errors,
  );
  if (sizingIncrementMW > maxIsoGrossMWPerUnit) {
    errors.push('Sizing increment cannot exceed the maximum ISO gross output per block.');
  }
  return errors.length > 0
    ? { value: null, errors }
    : {
        value: {
          blockCount,
          ccsEnabled,
          sizingIncrementMW,
          maxIsoGrossMWPerUnit,
        },
        errors,
      };
}

export function buildUniformCandidate(
  source: GenerationImpactScenarioInput,
  basis: UniformBasis,
  blockCount: number,
  isoGrossMWPerUnit: number,
  ccsEnabled: boolean,
): GenerationImpactScenarioInput {
  const candidate = cloneScenario(source);
  const auxiliaryLoadMWPerUnit = isoGrossMWPerUnit * basis.auxiliaryFractionOfIsoGross;
  candidate.name = `${blockCount} × ${isoGrossMWPerUnit} MW ${basis.template.technology}${ccsEnabled ? ' + CCS' : ''}`;
  candidate.units = Array.from({ length: blockCount }, (_, index): GenerationUnit => ({
    ...structuredClone(basis.template),
    id: `alternative-unit-${index + 1}`,
    label: `${basis.template.technology === 'combined-cycle-block' ? 'Block' : 'Unit'} ${index + 1}`,
    contingencyGroupId: `alternative-group-${index + 1}`,
    count: 1,
    isoGrossMWPerUnit,
    auxiliaryLoadMWPerUnit,
  }));
  candidate.ccs = normalizedCcs(source.ccs, ccsEnabled);
  candidate.assumptions = ccsAssumptionsForCandidate(source, candidate.ccs);
  if (source.equipmentBasis) {
    delete candidate.equipmentBasis;
    candidate.assumptions = candidate.assumptions.filter(assumption => (
      assumption.field !== 'technologyArchetype'
      && !assumption.field.startsWith('equipment')
    ));
    candidate.assumptions.push({
      field: 'equipmentCatalog',
      value: false,
      source: 'planning-default',
      label: 'Vendor equipment basis intentionally cleared for capacity-only alternative',
      citation: 'The rounded alternative duty is not an OEM-available rating; select and re-apply an equipment envelope after choosing a capacity configuration.',
      confidence: 'high',
    });
  }
  if (source.projectDutyBasis) {
    delete candidate.projectDutyBasis;
    delete candidate.footprint.installedPlantAcres;
    delete candidate.water.generationDesignDay;
    delete candidate.noiseSources;
    candidate.assumptions = candidate.assumptions.filter(
      assumption => !assumption.field.startsWith('projectDuty'),
    );
    candidate.assumptions.push({
      field: 'projectDutyDossier',
      value: false,
      source: 'planning-default',
      label: 'Project duty dossier intentionally cleared for resized capacity alternative',
      citation: 'Installed envelopes and absolute design-day duties are valid only for the documented block quantity and rating; re-apply a matching project dossier after selecting an alternative.',
      confidence: 'high',
    });
  }
  return candidate;
}

/**
 * Restore the immutable catalog screen before changing block quantity or
 * rating. Project absolute duties are configuration-specific and cannot be
 * silently carried into a capacity-only alternative.
 */
export function capacityOnlyOptimizationSource(
  input: GenerationImpactScenarioInput,
): GenerationImpactScenarioInput {
  const project = input.projectDutyBasis;
  if (!project) return input;
  const equipment = input.equipmentBasis;
  if (!equipment) {
    throw new RangeError(
      'Project duty alternatives require the catalog equipment basis to which the dossier is bound.',
    );
  }
  const restored = applyGenerationEquipmentEnvelope(
    input,
    equipment.catalogId,
    { allowUnconfirmed: true },
  );
  return restored;
}

function assertSolvedCandidate(result: GenerationImpactResult, context: string): void {
  if (result.gaps.length > 0) {
    throw new RangeError(`${context} is indeterminate: ${result.gaps.join(' ')}`);
  }
  const finiteValues = [
    result.capacity.normalNetMW,
    result.capacity.nMinusOneNetMW,
    result.capacity.nMinusOneMarginMW,
    result.capacity.nMinusOneReserveMarginMW,
    result.gas.peakDthPerDay,
    result.water.generationConsumptionGalPerDay,
    result.water.ccsConsumptionGalPerDay,
    result.emissions.uncontrolledCo2TonnesPerYear,
    result.emissions.capturedCo2TonnesPerYear,
    result.emissions.residualCo2TonnesPerYear,
    ...(result.emissions.noxTonnesPerYear == null
      ? []
      : [result.emissions.noxTonnesPerYear]),
    ...(result.emissions.coTonnesPerYear == null
      ? []
      : [result.emissions.coTonnesPerYear]),
    result.footprint.totalAcres,
  ];
  if (!finiteValues.every(Number.isFinite)) {
    throw new RangeError(`${context} produced a non-finite engineering result.`);
  }
}

function criterionMargin(
  result: GenerationImpactResult,
  criterion: GenerationSizingCriterion['criterion'],
): number {
  return criterion === 'n-1'
    ? result.capacity.nMinusOneMarginMW
    : result.capacity.nMinusOneReserveMarginMW;
}

function roundUp(value: number, incrementMW: number): number {
  const nearestIncrement = Math.round(value / incrementMW) * incrementMW;
  if (Math.abs(value - nearestIncrement) <= ROOT_TOLERANCE_MW) {
    return nearestIncrement;
  }
  return Math.ceil(value / incrementMW) * incrementMW;
}

function solveMinimum(
  input: GenerationImpactScenarioInput,
  basis: UniformBasis,
  options: NormalizedSizingOptions,
  criterion: GenerationSizingCriterion['criterion'],
): SizingSolution {
  const solveAt = (isoGrossMWPerUnit: number): GenerationImpactResult => {
    const result = solveGenerationImpact(buildUniformCandidate(
      input,
      basis,
      options.blockCount,
      isoGrossMWPerUnit,
      options.ccsEnabled,
    ));
    assertSolvedCandidate(result, `${options.blockCount}-block ${criterion} sizing`);
    return result;
  };

  let lowerMW = 0;
  let upperMW = Math.min(
    Math.max(basis.template.isoGrossMWPerUnit, options.sizingIncrementMW),
    options.maxIsoGrossMWPerUnit,
  );
  let upperResult = solveAt(upperMW);
  while (criterionMargin(upperResult, criterion) < 0 && upperMW < options.maxIsoGrossMWPerUnit) {
    lowerMW = upperMW;
    upperMW = Math.min(upperMW * 2, options.maxIsoGrossMWPerUnit);
    upperResult = solveAt(upperMW);
  }
  if (criterionMargin(upperResult, criterion) < 0) {
    throw new RangeError(
      `${options.blockCount}-block ${criterion} sizing exceeds the ${options.maxIsoGrossMWPerUnit} MW per-block search ceiling.`,
    );
  }

  for (let iteration = 0; iteration < ROOT_ITERATIONS && upperMW - lowerMW > ROOT_TOLERANCE_MW; iteration += 1) {
    const midpointMW = (lowerMW + upperMW) / 2;
    const midpointResult = solveAt(midpointMW);
    if (criterionMargin(midpointResult, criterion) >= 0) upperMW = midpointMW;
    else lowerMW = midpointMW;
  }

  const exactMinimumIsoGrossMWPerUnit = upperMW;
  let roundedIsoGrossMWPerUnit = roundUp(
    exactMinimumIsoGrossMWPerUnit,
    options.sizingIncrementMW,
  );
  if (roundedIsoGrossMWPerUnit > options.maxIsoGrossMWPerUnit) {
    throw new RangeError(
      `${options.blockCount}-block ${criterion} rounded sizing exceeds the ${options.maxIsoGrossMWPerUnit} MW per-block search ceiling.`,
    );
  }
  let roundedResult = solveAt(roundedIsoGrossMWPerUnit);
  if (criterionMargin(roundedResult, criterion) < 0) {
    roundedIsoGrossMWPerUnit += options.sizingIncrementMW;
    if (roundedIsoGrossMWPerUnit > options.maxIsoGrossMWPerUnit) {
      throw new RangeError(
        `${options.blockCount}-block ${criterion} rounded sizing exceeds the ${options.maxIsoGrossMWPerUnit} MW per-block search ceiling.`,
      );
    }
    roundedResult = solveAt(roundedIsoGrossMWPerUnit);
  }
  if (criterionMargin(roundedResult, criterion) < 0) {
    throw new Error(`${options.blockCount}-block ${criterion} rounded sizing failed verification.`);
  }
  return { exactMinimumIsoGrossMWPerUnit, roundedIsoGrossMWPerUnit, roundedResult };
}

function heldConstantDisclosures(
  input: GenerationImpactScenarioInput,
  basis: UniformBasis,
): readonly string[] {
  const reserveMarginPct = input.reserveMarginPct ?? 15;
  return [
    `Technology, ${basis.template.ambientFactor.toFixed(4)} ambient factor, ${basis.template.degradationFactor.toFixed(4)} degradation factor, and ${basis.template.heatRateBtuPerKWhHhv.toFixed(0)} Btu/kWh HHV heat rate are held constant from the entered fleet.`,
    `Auxiliary load scales in direct proportion to ISO gross rating at ${(basis.auxiliaryFractionOfIsoGross * 100).toFixed(3)}% per block.`,
    `Critical load (${(input.criticalLoadMW ?? input.facilityLoadMW).toFixed(1)} MW), reserve margin (${reserveMarginPct.toFixed(1)}%), annual operating hours, water intensities, and footprint intensities are held constant.`,
    'CCS-off rows retain the entered capture basis but disable capture; CCS-on rows preserve an edited basis or hydrate the explicit platform planning basis when the entered disabled placeholder is all zero.',
    'Each optimized block is assigned an independent contingency group; the largest single block is removed for N-1.',
    ...(input.equipmentBasis
      ? ['The configured row retains its selected equipment provenance; optimized capacity-only rows clear it because their rounded duties are not vendor-backed ratings.']
      : []),
    ...(input.projectDutyBasis
      ? ['The configured row retains its hash-bound project duty dossier; optimized rows restore the underlying catalog screen and clear project-specific performance, water, acoustics, and installed reservations.']
      : []),
  ];
}

function invalidSizing(errors: readonly string[]): InvalidGenerationSizing {
  return {
    status: 'invalid',
    modelVersion: GENERATION_ALTERNATIVES_MODEL_VERSION,
    errors: [...errors],
  };
}

/**
 * Calculate exact and screening-rounded ISO gross MW per uniform block.
 *
 * Candidate scenarios are deep clones. Capacity is always evaluated by the
 * canonical generation solver, including ambient/degradation derates,
 * proportional auxiliaries, and capture/compression parasitic power.
 */
export function sizeGenerationBlocks(
  input: GenerationImpactScenarioInput,
  options: GenerationBlockSizingOptions = {},
): GenerationBlockSizingResult {
  const { basis, errors: sourceErrors } = validateSource(input);
  if (!basis) return invalidSizing(sourceErrors);
  const normalized = normalizeSizingOptions(basis, input, options);
  if (!normalized.value) return invalidSizing([...sourceErrors, ...normalized.errors]);
  if (sourceErrors.length > 0) return invalidSizing(sourceErrors);

  try {
    const nMinusOne = solveMinimum(input, basis, normalized.value, 'n-1');
    const nMinusOnePlusReserve = solveMinimum(
      input,
      basis,
      normalized.value,
      'n-1-plus-reserve',
    );
    const reserveRequiredMW = nMinusOnePlusReserve.roundedResult.capacity.reserveRequiredMW;
    return {
      status: 'available',
      modelVersion: GENERATION_ALTERNATIVES_MODEL_VERSION,
      blockCount: normalized.value.blockCount,
      onlineBlocksAfterContingency: normalized.value.blockCount - 1,
      ccsEnabled: normalized.value.ccsEnabled,
      technology: basis.template.technology,
      auxiliaryFractionOfIsoGross: basis.auxiliaryFractionOfIsoGross,
      sizingIncrementMW: normalized.value.sizingIncrementMW,
      nMinusOne: {
        criterion: 'n-1',
        requiredNetMW: nMinusOne.roundedResult.capacity.criticalLoadMW,
        exactMinimumIsoGrossMWPerUnit: nMinusOne.exactMinimumIsoGrossMWPerUnit,
        roundedIsoGrossMWPerUnit: nMinusOne.roundedIsoGrossMWPerUnit,
        roundedNMinusOneNetMW: nMinusOne.roundedResult.capacity.nMinusOneNetMW,
        roundedMarginMW: nMinusOne.roundedResult.capacity.nMinusOneMarginMW,
        roundedVerdict: nMinusOne.roundedResult.capacity.nMinusOneVerdict,
      },
      nMinusOnePlusReserve: {
        criterion: 'n-1-plus-reserve',
        requiredNetMW: reserveRequiredMW,
        exactMinimumIsoGrossMWPerUnit: nMinusOnePlusReserve.exactMinimumIsoGrossMWPerUnit,
        roundedIsoGrossMWPerUnit: nMinusOnePlusReserve.roundedIsoGrossMWPerUnit,
        roundedNMinusOneNetMW: nMinusOnePlusReserve.roundedResult.capacity.nMinusOneNetMW,
        roundedMarginMW: nMinusOnePlusReserve.roundedResult.capacity.nMinusOneReserveMarginMW,
        roundedVerdict: nMinusOnePlusReserve.roundedResult.capacity.nMinusOneReserveVerdict,
      },
      heldConstantDisclosures: heldConstantDisclosures(input, basis),
    };
  } catch (error) {
    return invalidSizing([(error as Error).message]);
  }
}

function copyRowMetrics(result: GenerationImpactResult): Pick<
  GenerationAlternativeRow,
  'capacity' | 'gas' | 'water' | 'emissions' | 'footprint' | 'gaps' | 'warnings'
> {
  return {
    capacity: { ...result.capacity },
    gas: { ...result.gas },
    water: { ...result.water },
    emissions: { ...result.emissions },
    footprint: { ...result.footprint },
    gaps: [...result.gaps],
    warnings: [...result.warnings],
  };
}

function currentRow(
  input: GenerationImpactScenarioInput,
  basis: UniformBasis,
  result: GenerationImpactResult,
): GenerationAlternativeRow {
  return {
    id: 'current',
    kind: 'current',
    label: `Current · ${basis.currentBlockCount} × ${basis.template.isoGrossMWPerUnit} MW · ${input.ccs.enabled ? 'CCS on' : 'CCS off'}`,
    blockCount: basis.currentBlockCount,
    ccsEnabled: input.ccs.enabled,
    isoGrossMWPerUnit: basis.template.isoGrossMWPerUnit,
    totalIsoGrossMW: basis.currentBlockCount * basis.template.isoGrossMWPerUnit,
    exactReserveMinimumIsoGrossMWPerUnit: null,
    sizingIncrementMW: null,
    input: cloneScenario(input),
    result: structuredClone(result),
    ...copyRowMetrics(result),
  };
}

function isPhysicallyIdenticalToCurrent(
  input: GenerationImpactScenarioInput,
  basis: UniformBasis,
  blockCount: number,
  ccsEnabled: boolean,
  isoGrossMWPerUnit: number,
): boolean {
  return blockCount === basis.currentBlockCount
    && ccsEnabled === input.ccs.enabled
    && nearlyEqual(isoGrossMWPerUnit, basis.template.isoGrossMWPerUnit);
}

function invalidMatrix(errors: readonly string[]): InvalidGenerationAlternativesMatrix {
  return {
    status: 'invalid',
    modelVersion: GENERATION_ALTERNATIVES_MODEL_VERSION,
    errors: [...errors],
  };
}

function equipmentHeldConstantDisclosures(
  input: GenerationImpactScenarioInput,
  selection: ActiveGenerationEquipmentSelection,
): readonly string[] {
  const reserveMarginPct = input.reserveMarginPct ?? 15;
  return [
    `Block rating, derates, auxiliaries, and heat rate are fixed by the applied ${selection.manufacturer} ${selection.model} envelope; only block quantity and CCS mode vary.`,
    `Critical load (${(input.criticalLoadMW ?? input.facilityLoadMW).toFixed(1)} MW), reserve margin (${reserveMarginPct.toFixed(1)}%), and annual operating hours are held constant.`,
    'Rows that fail the contingency or reserve screen are shown with their real verdicts — the fixed block rating is never resized to force a pass.',
    'CCS-off rows retain the entered capture basis but disable capture; CCS-on rows preserve an edited basis or hydrate the explicit platform planning basis when the entered disabled placeholder is all zero.',
    'Each block is an independent contingency group; the largest single block is removed for the contingency screen.',
    ...(input.projectDutyBasis
      ? ['The configured row retains its hash-bound project duty dossier; count alternatives restore the underlying catalog screen and clear project-specific performance, water, acoustics, and installed reservations.']
      : []),
  ];
}

/**
 * Count-only alternatives for an applied equipment envelope: every row is a
 * REAL re-application of the envelope at k blocks (valid basis + fingerprint),
 * never a rounded generic duty. Failing counts are reported honestly.
 */
function buildEquipmentCountRows(
  candidateSource: GenerationImpactScenarioInput,
  selection: ActiveGenerationEquipmentSelection,
  currentInput: GenerationImpactScenarioInput,
  basis: UniformBasis,
  candidateBlockCounts: readonly number[],
  ccsModes: readonly boolean[],
): GenerationAlternativeRow[] | InvalidGenerationAlternativesMatrix {
  const rows: GenerationAlternativeRow[] = [];
  for (const blockCount of candidateBlockCounts) {
    for (const ccsEnabled of ccsModes) {
      if (blockCount === basis.currentBlockCount && ccsEnabled === currentInput.ccs.enabled) {
        continue; // physically identical to the current row's duty
      }
      const context = `${blockCount} × ${selection.model} ${ccsEnabled ? 'CCS-on' : 'CCS-off'} alternative`;
      let resized: GenerationImpactScenarioInput;
      try {
        // CCS mode is set BEFORE the resize so the envelope apply stays the
        // last writer on every unit field.
        const ccsSource = cloneScenario(candidateSource);
        ccsSource.ccs = normalizedCcs(candidateSource.ccs, ccsEnabled);
        ccsSource.assumptions = ccsAssumptionsForCandidate(candidateSource, ccsSource.ccs);
        resized = resizeGenerationEquipmentFleet(ccsSource, blockCount);
      } catch (error) {
        return invalidMatrix([`${context}: ${(error as Error).message}`]);
      }
      resized.name = `${blockCount} × ${selection.model}${ccsEnabled ? ' + CCS' : ''}`;
      const result = solveGenerationImpact(resized);
      try {
        assertSolvedCandidate(result, context);
      } catch (error) {
        return invalidMatrix([(error as Error).message]);
      }
      rows.push({
        id: `equipment-${blockCount}-blocks-${ccsEnabled ? 'ccs-on' : 'ccs-off'}`,
        kind: 'equipment-count',
        equipmentModel: selection.model,
        label: `${blockCount} × ${selection.model} · ${ccsEnabled ? 'CCS on' : 'CCS off'}`,
        blockCount,
        ccsEnabled,
        isoGrossMWPerUnit: resized.units[0]?.isoGrossMWPerUnit ?? 0,
        totalIsoGrossMW: blockCount * (resized.units[0]?.isoGrossMWPerUnit ?? 0),
        exactReserveMinimumIsoGrossMWPerUnit: null,
        sizingIncrementMW: null,
        input: cloneScenario(resized),
        result: structuredClone(result),
        ...copyRowMetrics(result),
      });
    }
  }
  return rows;
}

/**
 * Build a deterministic current-plus-optimized alternatives matrix.
 *
 * Optimized rows are sized to N-1 plus reserve, rounded upward to the selected
 * screening increment, and re-solved before their resource metrics are copied.
 * Any invalid source/candidate returns no partial matrix.
 */
export function buildGenerationAlternativesMatrix(
  input: GenerationImpactScenarioInput,
  options: GenerationAlternativesOptions = {},
): GenerationAlternativesMatrixResult {
  const { basis, sourceResult, errors: sourceErrors } = validateSource(input);
  if (!basis || sourceErrors.length > 0) return invalidMatrix(sourceErrors);
  let candidateSource: GenerationImpactScenarioInput;
  try {
    candidateSource = capacityOnlyOptimizationSource(input);
  } catch (error) {
    return invalidMatrix([(error as Error).message]);
  }
  const {
    basis: candidateBasis,
    errors: candidateSourceErrors,
  } = validateSource(candidateSource);
  if (!candidateBasis || candidateSourceErrors.length > 0) {
    return invalidMatrix(candidateSourceErrors);
  }

  const candidateBlockCounts = [...new Set(
    options.candidateBlockCounts ?? DEFAULT_GENERATION_ALTERNATIVE_BLOCK_COUNTS,
  )].sort((left, right) => left - right);
  const ccsModes = [...new Set(
    options.ccsModes ?? DEFAULT_GENERATION_ALTERNATIVE_CCS_MODES,
  )].sort((left, right) => Number(left) - Number(right));
  const sizingIncrementMW = options.sizingIncrementMW
    ?? DEFAULT_GENERATION_ALTERNATIVE_SIZING_INCREMENT_MW;
  const maxIsoGrossMWPerUnit = options.maxIsoGrossMWPerUnit
    ?? MAX_ISO_GROSS_MW_PER_UNIT;
  const optionErrors: string[] = [];
  if (candidateBlockCounts.length === 0) optionErrors.push('At least one candidate block count is required.');
  for (const blockCount of candidateBlockCounts) {
    if (!Number.isInteger(blockCount) || blockCount < 2 || blockCount > MAX_BLOCK_COUNT) {
      optionErrors.push(`Block count must be an integer between 2 and ${MAX_BLOCK_COUNT}; received ${blockCount}.`);
    }
  }
  if (ccsModes.length === 0 || ccsModes.some((mode) => typeof mode !== 'boolean')) {
    optionErrors.push('At least one boolean CCS mode is required.');
  }
  validatePositiveOption(
    sizingIncrementMW,
    'Sizing increment',
    MAX_ISO_GROSS_MW_PER_UNIT,
    optionErrors,
  );
  validatePositiveOption(
    maxIsoGrossMWPerUnit,
    'Maximum ISO gross output per block',
    MAX_ISO_GROSS_MW_PER_UNIT,
    optionErrors,
  );
  if (sizingIncrementMW > maxIsoGrossMWPerUnit) {
    optionErrors.push('Sizing increment cannot exceed the maximum ISO gross output per block.');
  }
  if (optionErrors.length > 0) return invalidMatrix([...new Set(optionErrors)]);

  // EQUIPMENT MODE — an applied envelope (catalog or verified user entry)
  // fixes the block rating, so alternatives become counts of the REAL block
  // via the one sanctioned apply path. The generic rounded-duty path below is
  // byte-identical for scenarios without an equipment selection.
  const equipmentSelection = activeGenerationEquipmentSelection(input);
  if (equipmentSelection) {
    const equipmentRows = buildEquipmentCountRows(
      candidateSource,
      equipmentSelection,
      input,
      basis,
      candidateBlockCounts,
      ccsModes,
    );
    if (!Array.isArray(equipmentRows)) return equipmentRows;
    return {
      status: 'available',
      modelVersion: GENERATION_ALTERNATIVES_MODEL_VERSION,
      sourceScenarioName: input.name,
      sizingIncrementMW,
      heldConstantDisclosures: equipmentHeldConstantDisclosures(input, equipmentSelection),
      rows: [currentRow(input, basis, sourceResult), ...equipmentRows],
    };
  }

  const rows: GenerationAlternativeRow[] = [currentRow(input, basis, sourceResult)];
  for (const blockCount of candidateBlockCounts) {
    for (const ccsEnabled of ccsModes) {
      const sizing = sizeGenerationBlocks(candidateSource, {
        blockCount,
        ccsEnabled,
        sizingIncrementMW,
        maxIsoGrossMWPerUnit,
      });
      if (sizing.status === 'invalid') {
        return invalidMatrix(sizing.errors.map(
          (error) => `${blockCount}-block ${ccsEnabled ? 'CCS-on' : 'CCS-off'} alternative: ${error}`,
        ));
      }
      const isoGrossMWPerUnit = sizing.nMinusOnePlusReserve.roundedIsoGrossMWPerUnit;
      const candidate = buildUniformCandidate(
        candidateSource,
        candidateBasis,
        blockCount,
        isoGrossMWPerUnit,
        ccsEnabled,
      );
      const result = solveGenerationImpact(candidate);
      try {
        assertSolvedCandidate(result, `${blockCount}-block ${ccsEnabled ? 'CCS-on' : 'CCS-off'} alternative`);
      } catch (error) {
        return invalidMatrix([(error as Error).message]);
      }
      if (result.capacity.nMinusOneReserveVerdict !== 'pass') {
        return invalidMatrix([
          `${blockCount}-block ${ccsEnabled ? 'CCS-on' : 'CCS-off'} alternative failed reserve-duty verification after round-up.`,
        ]);
      }
      if (isPhysicallyIdenticalToCurrent(
        input,
        basis,
        blockCount,
        ccsEnabled,
        isoGrossMWPerUnit,
      )) {
        continue;
      }
      rows.push({
        id: `optimized-${blockCount}-blocks-${ccsEnabled ? 'ccs-on' : 'ccs-off'}`,
        kind: 'optimized',
        label: `Optimized · ${blockCount} × ${isoGrossMWPerUnit} MW · ${ccsEnabled ? 'CCS on' : 'CCS off'}`,
        blockCount,
        ccsEnabled,
        isoGrossMWPerUnit,
        totalIsoGrossMW: blockCount * isoGrossMWPerUnit,
        exactReserveMinimumIsoGrossMWPerUnit: sizing.nMinusOnePlusReserve.exactMinimumIsoGrossMWPerUnit,
        sizingIncrementMW,
        input: cloneScenario(candidate),
        result: structuredClone(result),
        ...copyRowMetrics(result),
      });
    }
  }

  return {
    status: 'available',
    modelVersion: GENERATION_ALTERNATIVES_MODEL_VERSION,
    sourceScenarioName: input.name,
    sizingIncrementMW,
    heldConstantDisclosures: heldConstantDisclosures(input, basis),
    rows,
  };
}
